import { useState, useEffect } from 'react';

const blobUrlCache = new Map();
const pendingRequests = new Map();
const subscribers = new Set();

function notifySubscribers() {
  subscribers.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore listener errors
    }
  });
}

function resolveFullAvatarUrl(avatarUrl) {
  let fullUrl = avatarUrl.replace(/([?&])bearer=[^&]*(&|$)/, '$1').replace(/[?&]$/, '');
  if (fullUrl.startsWith('/api') && process.env.REACT_APP_BACKEND_URL) {
    const backendOrigin = process.env.REACT_APP_BACKEND_URL.replace(/\/api\/?$/, '');
    fullUrl = `${backendOrigin}${fullUrl}`;
  }
  return fullUrl;
}

function fetchAvatarBlobUrl(fullUrl) {
  if (pendingRequests.has(fullUrl)) {
    return pendingRequests.get(fullUrl);
  }

  const token = localStorage.getItem(process.env.REACT_APP_TOKEN_NAME);
  if (!token) {
    return Promise.resolve(null);
  }

  const requestPromise = fetch(fullUrl, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Requested-With': 'XMLHttpRequest',
    },
  })
    .then(async (res) => {
      if (!res.ok) {
        blobUrlCache.set(fullUrl, null);
        return null;
      }
      const blob = await res.blob();
      if (!blob || blob.size === 0) {
        blobUrlCache.set(fullUrl, null);
        return null;
      }
      const objectUrl = URL.createObjectURL(blob);
      blobUrlCache.set(fullUrl, objectUrl);
      notifySubscribers();
      return objectUrl;
    })
    .catch(() => {
      blobUrlCache.set(fullUrl, null);
      return null;
    })
    .finally(() => {
      pendingRequests.delete(fullUrl);
    });

  pendingRequests.set(fullUrl, requestPromise);
  return requestPromise;
}

/**
 * React hook that subscribes a component to avatar blob URL updates
 * so that `getAuthenticatedAvatarUrl` seamlessly updates when fetched.
 */
export function useAvatarBlobSubscription() {
  const [, setTick] = useState(0);

  useEffect(() => {
    const listener = () => setTick((t) => t + 1);
    subscribers.add(listener);
    return () => {
      subscribers.delete(listener);
    };
  }, []);
}

/**
 * Invalidates cached blob URLs matching the given user ID or URL substring.
 *
 * @param {string|number} userIdOrSubstring
 */
export function invalidateAvatarCache(userIdOrSubstring) {
  const needle = typeof userIdOrSubstring === 'number'
    ? `/user/${userIdOrSubstring}/avatar`
    : String(userIdOrSubstring || '');

  if (!needle) return;

  Array.from(blobUrlCache.keys()).forEach((key) => {
    if (key.includes(needle)) {
      const oldBlobUrl = blobUrlCache.get(key);
      if (oldBlobUrl && typeof oldBlobUrl === 'string' && oldBlobUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(oldBlobUrl);
        } catch {
          // Ignore revoke errors
        }
      }
      blobUrlCache.delete(key);
    }
  });
  notifySubscribers();
}

/**
 * Clears all cached avatar blob URLs in memory (e.g. on logout or user switch).
 */
export function clearAllAvatarCache() {
  Array.from(blobUrlCache.keys()).forEach((key) => {
    const oldBlobUrl = blobUrlCache.get(key);
    if (oldBlobUrl && typeof oldBlobUrl === 'string' && oldBlobUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(oldBlobUrl);
      } catch {
        // Ignore revoke errors
      }
    }
    blobUrlCache.delete(key);
  });
  notifySubscribers();
}

/**
 * Resolves an avatar URL into an in-memory `blob:` URL fetched securely via
 * Authorization header + XHR (never exposing the JWT token in the URL).
 *
 * @param {string|null|undefined} avatarUrl
 * @returns {string|null}
 */
export function getAuthenticatedAvatarUrl(avatarUrl) {
  if (!avatarUrl || typeof avatarUrl !== 'string') return null;
  if (avatarUrl.startsWith('data:') || avatarUrl.startsWith('blob:')) {
    return avatarUrl;
  }

  const fullUrl = resolveFullAvatarUrl(avatarUrl);
  if (blobUrlCache.has(fullUrl)) {
    return blobUrlCache.get(fullUrl);
  }

  fetchAvatarBlobUrl(fullUrl);
  return null;
}
